import axios from 'axios';
import log from 'loglevel';
import AuthService from './AuthService';
import config from '../../config';

const { signalR } = window;

let hubConnection;
let state = 'disconnected';
const startupEventHandlers = [];
let connectionId = null;

function getAxiosConfig() {
  const configuration = {
    headers: {
      'x-ms-signalr-userid': AuthService.getUserId(),
      Authorization: `Bearer ${AuthService.getToken()}`,
    },
  };
  return configuration;
}

function getConnectionInfo() {
  return axios
    .post(`${config.appUrls.signalr}/api/negotiate`, null, getAxiosConfig())
    .then(resp => resp.data);
}

function addUserToGroup() {
  return axios.post(
    `${config.appUrls.signalr}/api/addToGroup`,
    {
      connectionId,
    },
    getAxiosConfig(),
  );
}

const startConnection = () => {
  getConnectionInfo()
    .then(info => {
      // make compatible with old and new SignalRConnectionInfo
      const accessToken = info.accessToken || info.accessKey;
      const url = info.url || info.endpoint;

      const options = {
        accessTokenFactory: () => accessToken,
      };

      hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(url, options)
        .configureLogging(signalR.LogLevel.Warning) // signalR.LogLevel.Trace logs everything, including data transported between hub and client.
        .build();
      hubConnection.onclose(error => {
        state = 'disconnected';
        if (error) {
          log.warn('SignalR connection closed with error:', error);
        } else {
          log.info('SignalR connection closed gracefully');
        }

        // Only reconnect if there was an error (not during intentional shutdown)
        if (error) {
          log.info('Attempting to reconnect SignalR after error...');
          setTimeout(() => startConnection(), 5000);
        }
      });

      state = 'connecting';

      hubConnection
        .start()
        .then(() => {
          ({ connectionId } = hubConnection);

          state = 'connected';

          addUserToGroup();

          if (startupEventHandlers.length > 0) {
            startupEventHandlers.map(eventHandler => {
              hubConnection.on(eventHandler.name, eventHandler.action);
            });
          }
        })
        .catch(error => {
          log.error(error);
          state = 'disconnected';
          // Try to reconnect after 5 seconds
          setTimeout(() => startConnection(), 5000);
        });
    })
    .catch(error => {
      log.error(error);
      state = 'disconnected';
    });
};

const subscribe = (name, action) => {
  // If the connection was not yet established, add it to an array which will be used
  // To add event handlers to the connection when the connection succeeded
  if (state !== 'connected') {
    startupEventHandlers.push({
      name,
      action,
    });
  } else {
    hubConnection.on(name, action);
  }
};

const unsubscribe = (name, action) => {
  if (hubConnection) {
    hubConnection.off(name, action);
  }
};

const stopConnection = () => {
  if (hubConnection && (state === 'connected' || state === 'connecting')) {
    state = 'disconnecting';

    log.info('Stopping SignalR connection...');

    return hubConnection
      .stop()
      .then(() => {
        log.info('SignalR connection stopped gracefully');
        state = 'disconnected';
        hubConnection = null;
        connectionId = null;
      })
      .catch(error => {
        log.error('Error stopping SignalR connection:', error);
        state = 'disconnected';
        hubConnection = null;
        connectionId = null;
      });
  }
};

export default {
  startConnection,
  stopConnection,
  subscribe,
  unsubscribe,
  getConnectionId: () => connectionId,
  getConnectionState: () => state,
};
